// White-label theme bundle (CB-12, CB-16). This directory is the ONLY place brand
// identity and design tokens may be hardcoded (INF-1 / gate exemption for theme/). A
// different tenant swaps these files; nothing else in the surface changes.
//
// This file is the typed mirror of theme.css for TypeScript consumers. Colours are
// exposed as `var(--…)` references, never hex: the value lives once, in theme.css,
// and follows the active light/dark theme (DR-45, FR-X9). Status chips map the
// canonical tone from @bookingdei/i18n/status-tone onto this surface's tone tokens
// (CB-15, DC08) — the old per-status hex map is gone.
import { statusToneOrNeutral } from '@bookingdei/i18n/status-tone';
import type { StatusTone } from '@bookingdei/i18n/status-tone';
import type { StatusEntity } from '@bookingdei/i18n/status-label';

export const brand = {
  name: 'BookingDei',
  vendor: 'BetterDots',
  version: 'v0.1 demo',
  tagline_en: 'Your bookings, all in one place',
  tagline_bn: 'আপনার সব বুকিং এক জায়গায়',
} as const;

// `<meta name="theme-color">` per resolved theme — the page canvas, so the browser
// chrome blends with the page. Mirrored by the pre-paint script in index.html.
export const themeColor = {
  light: '#E5EFFC',
  dark: '#0A121C',
} as const;

const v = <N extends string>(name: N) => `var(--${name})` as const;

// Semantic colour tokens (design names, bd-core.js LIGHT/DARK maps).
export const color = {
  canvas: v('canvas'),
  page: v('page'),
  bg: v('color-bg'),
  panel2: v('panel2'),
  surface: v('color-surface'),
  brd: v('brd'),
  hair: v('hair'),
  divider: v('color-divider'),
  rowHover: v('row-hover'),
  text: v('color-text'),
  muted: v('muted'),
  muted2: v('muted2'),
  accent: v('color-accent'),
  onAccent: v('on-accent'),
  accentSoft: v('accent-soft'),
  accent100: v('color-accent-100'),
  accent200: v('color-accent-200'),
  accent600: v('color-accent-600'),
  accent700: v('color-accent-700'),
  accent800: v('color-accent-800'),
  navy: v('color-navy'),
  ok: v('c-ok'),
  okFg: v('c-ok-fg'),
  okSoft: v('c-ok-soft'),
  warn: v('c-warn'),
  warnFg: v('c-warn-fg'),
  warnSoft: v('c-warn-soft'),
  info: v('c-info'),
  infoFg: v('c-info-fg'),
  infoSoft: v('c-info-soft'),
  danger: v('danger'),
  dangerSoft: v('danger-soft'),
  dangerStrong: v('danger-strong'),
  n100: v('n100'),
  n200: v('n200'),
  n300: v('n300'),
  n400: v('n400'),
  n600: v('n600'),
  n800: v('n800'),
  n900: v('n900'),
  glass1: v('glass-1'),
  glass2: v('glass-2'),
  glassBrd: v('glass-brd'),
  onTone: v('on-tone'),
  scrim: v('scrim'),
  meterOff: v('meter-off'),
} as const;

export const radius = {
  card: v('r-card'),
  nest: v('r-nest'),
  pill: v('r-pill'),
} as const;

// Note: `shadow.pill` is the design's `--pill` control ring — a SHADOW, not a radius.
export const shadow = {
  pill: v('pill'),
  pillGlow: v('pill-glow'),
  lg: v('shd-lg'),
  hover: v('shd-h'),
} as const;

export const motion = {
  micro: v('t-micro'),
  fast: v('t-fast'),
  base: v('t-base'),
  slow: v('t-slow'),
  enter: v('e-enter'),
  exit: v('e-exit'),
  move: v('e-move'),
} as const;

// Motion scale in ms, for code that needs the number (timers that wait out an exit).
export const motionMs = { micro: 90, fast: 140, base: 200, slow: 280, reduced: 80 } as const;

// Layout breakpoint of the customer design (`window.innerWidth < 760`, VIS §4).
export const MOBILE_MAX_WIDTH = 759;

export interface ToneColours {
  fg: string;
  bg: string;
  dot: string;
}

// Design TONE map (bd-core.js L180-186) → theme.css `--tone-*` vars. `accent` is a
// design tone the canonical status-tone table never returns; kept for non-status chips.
export type Tone = StatusTone | 'accent';

const toneVars = (tone: Tone): ToneColours => ({
  fg: `var(--tone-${tone}-fg)`,
  bg: `var(--tone-${tone}-bg)`,
  dot: `var(--tone-${tone}-dot)`,
});

export const TONE: Readonly<Record<Tone, ToneColours>> = {
  ok: toneVars('ok'),
  warn: toneVars('warn'),
  info: toneVars('info'),
  danger: toneVars('danger'),
  accent: toneVars('accent'),
  neutral: toneVars('neutral'),
};

export function toneColours(tone: Tone): ToneColours {
  return TONE[tone] ?? TONE.neutral;
}

// Colours for any canonical status value; unknown wire values (AP-D10) are neutral.
export function statusColours(entity: StatusEntity, value: string): ToneColours {
  return toneColours(statusToneOrNeutral(entity, value));
}
